import { useCallback, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useSSEConnectionContext } from '@/infra/sse'

interface AuthActionsState {
  readonly isLoggingIn: boolean
  readonly isLoggingOut: boolean
  readonly loginError: string | null
}

export interface UseAuthActionsReturn extends AuthActionsState {
  readonly login: () => Promise<void>
  readonly logout: () => Promise<void>
  readonly clearError: () => void
}

export function useAuthActions(): UseAuthActionsReturn {
  const auth = useAuth()
  const { disconnect } = useSSEConnectionContext()
  const [state, setState] = useState<AuthActionsState>({
    isLoggingIn: false,
    isLoggingOut: false,
    loginError: null,
  })

  const login = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoggingIn: true, loginError: null }))
    try {
      await auth.signinRedirect()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed'
      setState((prev) => ({ ...prev, isLoggingIn: false, loginError: message }))
    }
  }, [auth])

  const logout = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoggingOut: true }))
    try {
      disconnect()
      await auth.signoutRedirect()
    } catch {
      setState((prev) => ({ ...prev, isLoggingOut: false }))
    }
  }, [auth, disconnect])

  const clearError = useCallback((): void => {
    setState((prev) => ({ ...prev, loginError: null }))
  }, [])

  return {
    ...state,
    login,
    logout,
    clearError,
  }
}
